import { DeleteOutlined, EditOutlined, MoreOutlined, UserOutlined } from "@ant-design/icons";
import { Checkbox, Dropdown, Tag, Typography, type MenuProps } from "antd";
import dayjs from "dayjs";

import "dayjs/locale/pl";

import React, { useState } from "react";
import { Link } from "react-router-dom";

import { TeamMemberTaskStatus, type TeamMemberTaskWithTrainee } from "@fitness-app/data-models/entities/TeamMemberTask";

import { useTeamMemberTaskContext } from "../context/TeamMemberTaskContext";
import { useDeleteTeamMemberTask } from "../hooks/useDeleteTeamMemberTask";
import { useUpdateTeamMemberTask } from "../hooks/useUpdateTeamMemberTask";
import { categoryColors, getCategoryLabel, getPriorityColor, priorityBorderColors } from "../types";

dayjs.locale("pl");

interface TeamMemberTaskItemProps {
  task: TeamMemberTaskWithTrainee;
  showDate?: boolean;
}

export const TeamMemberTaskItem: React.FC<TeamMemberTaskItemProps> = ({ task, showDate = false }) => {
  const { editTask } = useTeamMemberTaskContext();
  const { toggleComplete, isLoading: isUpdating } = useUpdateTeamMemberTask();
  const { confirmDelete } = useDeleteTeamMemberTask();

  const [isAnimating, setIsAnimating] = useState(false);
  const [isExiting, setIsExiting] = useState(false);

  const isCompleted = task.status === TeamMemberTaskStatus.Completed;
  const dueDateStr = task.dueDate?.date;
  const isOverdue = dueDateStr && dayjs(dueDateStr).isBefore(dayjs().startOf("day")) && !isCompleted;
  const borderColorClass = priorityBorderColors[task.priority];

  const handleCheckboxChange = async () => {
    if (isCompleted) {
      // Uncompleting - no animation needed
      await toggleComplete(task);
    } else {
      // Completing - animate first
      setIsAnimating(true);

      // Wait for strikethrough animation
      setTimeout(() => {
        setIsExiting(true);

        // Wait for exit animation, then update
        setTimeout(async () => {
          await toggleComplete(task);
          setIsAnimating(false);
          setIsExiting(false);
        }, 300);
      }, 400);
    }
  };

  const menuItems: MenuProps["items"] = [
    {
      key: "edit",
      icon: <EditOutlined />,
      label: "Edytuj",
      onClick: () => editTask(task),
    },
    {
      type: "divider",
    },
    {
      key: "delete",
      icon: <DeleteOutlined />,
      label: "Usuń",
      danger: true,
      onClick: () => confirmDelete(task.id, task.title),
    },
  ];

  const formatTime = () => {
    // Show time range if both start and end times are set
    if (task.startDate?.time && task.endDate?.time) {
      return `${task.startDate.time} - ${task.endDate.time}`;
    }
    // Show due time if set
    if (task.dueDate?.time) {
      return task.dueDate.time;
    }
    return null;
  };

  const formatCompletedDate = () => {
    if (!isCompleted || !dueDateStr) return null;

    const dueDate = dayjs(dueDateStr);
    const today = dayjs().startOf("day");

    if (dueDate.isSame(today, "day")) {
      return "Dzisiaj";
    }
    if (dueDate.isSame(today.subtract(1, "day"), "day")) {
      return "Wczoraj";
    }
    return dueDate.format("D MMM");
  };

  const formatDueDate = () => {
    if (!dueDateStr) return null;

    const dueDate = dayjs(dueDateStr);
    const today = dayjs().startOf("day");

    if (dueDate.isSame(today, "day")) {
      return "Dzisiaj";
    }
    if (dueDate.isSame(today.add(1, "day"), "day")) {
      return "Jutro";
    }
    if (dueDate.isSame(today.subtract(1, "day"), "day")) {
      return "Wczoraj";
    }
    return dueDate.format("D MMM");
  };

  const timeDisplay = formatTime();
  const completedDateDisplay = formatCompletedDate();
  const dueDateDisplay = showDate ? formatDueDate() : null;

  return (
    <div
      className={`flex items-center gap-3 rounded-md border border-l-4 bg-white px-3 py-2 shadow-sm transition-all duration-300 hover:bg-gray-50 ${borderColorClass} ${
        isExiting ? "scale-95 opacity-0" : "scale-100 opacity-100"
      }`}
      style={{
        borderLeftColor: getPriorityColor(task.priority),
        maxHeight: isExiting ? "0px" : "200px",
        marginBottom: isExiting ? "0px" : undefined,
        paddingTop: isExiting ? "0px" : undefined,
        paddingBottom: isExiting ? "0px" : undefined,
        overflow: "hidden",
      }}
    >
      <Checkbox
        checked={isCompleted || isAnimating}
        onChange={handleCheckboxChange}
        disabled={isUpdating || isAnimating || isExiting}
        className="flex-shrink-0"
      />

      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="relative inline-block">
            <Typography.Text
              type={isOverdue ? "danger" : undefined}
              className={`font-medium transition-colors duration-300 ${
                isCompleted || isAnimating ? "text-gray-400" : ""
              }`}
            >
              {task.title}
            </Typography.Text>
            {/* Animated strikethrough line */}
            <span
              className="duration-400 absolute left-0 top-1/2 h-[1.5px] bg-gray-400 transition-all ease-out"
              style={{
                width: isCompleted || isAnimating ? "100%" : "0%",
                transitionDuration: "400ms",
              }}
            />
          </span>
          {dueDateDisplay && !isCompleted && (
            <Typography.Text type={isOverdue ? "danger" : "secondary"} className="text-xs">
              {dueDateDisplay}
            </Typography.Text>
          )}
          {completedDateDisplay && (
            <Typography.Text type="secondary" className="text-xs">
              {completedDateDisplay}
            </Typography.Text>
          )}
          {timeDisplay && (
            <Typography.Text type="success" strong className="text-xs">
              {timeDisplay}
            </Typography.Text>
          )}
        </div>

        {task.description && (
          <Typography.Text
            type="secondary"
            className={`mt-0.5 block text-xs transition-opacity duration-300 ${
              isAnimating ? "opacity-50" : "opacity-100"
            }`}
            ellipsis
          >
            {task.description}
          </Typography.Text>
        )}

        <div
          className={`mt-1 flex flex-wrap items-center gap-2 transition-opacity duration-300 ${
            isAnimating ? "opacity-50" : "opacity-100"
          }`}
        >
          {task.trainee && (
            <Link to={`/trainee/details/${task.trainee.id}`} onClick={(e) => e.stopPropagation()}>
              <Tag icon={<UserOutlined />} className="m-0 cursor-pointer hover:opacity-80">
                {task.trainee.firstName} {task.trainee.lastName}
              </Tag>
            </Link>
          )}
          {task.tags?.map((tag) => (
            <Tag key={tag} className="m-0">
              {tag}
            </Tag>
          ))}
        </div>
      </div>

      <div
        className={`flex flex-shrink-0 items-center gap-2 transition-opacity duration-300 ${
          isAnimating ? "opacity-50" : "opacity-100"
        }`}
      >
        <Tag color={categoryColors[task.category]} className="m-0">
          {getCategoryLabel(task.category)}
        </Tag>

        <Dropdown menu={{ items: menuItems }} trigger={["click"]} placement="bottomRight">
          <button className="flex h-8 w-8 items-center justify-center rounded hover:bg-gray-100">
            <MoreOutlined />
          </button>
        </Dropdown>
      </div>
    </div>
  );
};
